import { Badge, Button, Card, EmptyState, Input } from '@personachat/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, CreditCard, Download, ExternalLink, Filter, UserRound, Webhook } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components, paths } from '../../api/schema';
import { ErreurChargement, Filtre, Recherche, Section, Select } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { enregistrer, nomDate } from '../../lib/fichiers';
import { bornesJourParis, formatCredits, formatDate, formatDateTime, formatEuros, formatNombre, jourParis } from '../../lib/format';
import { lienStripe, PAR_PERIODE, STATUT_ABONNEMENT, STATUT_PAIEMENT, STATUT_WEBHOOK } from '../../lib/labels';
import { useFiltresUrl } from '../../lib/urlState';

import { useVentes, VentesEntete } from './VentesEntete';

type Abonnement = components['schemas']['Subscription'];
type Paiement = components['schemas']['Payment'];
type Webhook = components['schemas']['WebhookLog'];
type RequetePaiements = NonNullable<paths['/api/admin/billing/transactions']['get']['parameters']['query']>;
type RequeteWebhooks = NonNullable<paths['/api/admin/billing/webhooks']['get']['parameters']['query']>;

function LienExterne({ href, children }: { href: string | null; children: string }) {
  if (!href) return null;
  return (
    <a className="link nowrap" href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
      {children} <ExternalLink size={12} aria-hidden="true" />
    </a>
  );
}

function Eleve({ user }: { user: Paiement['user'] }) {
  const nom = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return (
    <div>
      <Link className="link cell-main" to={`/eleves/${user.id}`}>
        {nom || user.email}
      </Link>
      {nom && <div className="cell-sub">{user.email}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Abonnées
// ---------------------------------------------------------------------------

const FILTRES_ABONNEES = { statut: '', formule: '', eleve: '', page: '1' };

export function SubscribersPage() {
  const { valeurs: f, modifier, reinitialiser, actifs } = useFiltresUrl(FILTRES_ABONNEES);
  const vue = useVentes();
  const formules = useQuery({ queryKey: ['ventes', 'formules'], queryFn: async () => (await lire(api.GET('/api/admin/billing/plans'))).plans });
  const liste = useQuery({
    queryKey: ['ventes', 'abonnees', f],
    queryFn: () =>
      lire(
        api.GET('/api/admin/billing/subscriptions', {
          params: {
            query: {
              page: Number(f.page) || 1,
              limit: 50,
              ...(f.statut ? { status: f.statut as Abonnement['status'] } : {}),
              ...(f.formule ? { planId: f.formule } : {}),
              ...(f.eleve ? { q: f.eleve } : {}),
            },
          },
        })
      ),
    placeholderData: keepPreviousData,
  });
  const mode = vue.data?.stripeMode;

  const colonnes: Colonne<Abonnement>[] = [
    { id: 'eleve', header: 'Élève', cell: (a) => <Eleve user={a.user} /> },
    {
      id: 'formule',
      header: 'Formule',
      cell: (a) => (
        <div>
          <div>{a.plan.name}</div>
          <div className="cell-sub">
            {formatEuros(a.plan.priceCents)} {PAR_PERIODE[a.plan.interval]}
          </div>
        </div>
      ),
    },
    {
      id: 'statut',
      header: 'Statut',
      cell: (a) => {
        const s = STATUT_ABONNEMENT[a.status] ?? { libelle: a.status, ton: 'neutral' as const };
        return <Badge tone={s.ton}>{s.libelle}</Badge>;
      },
    },
    { id: 'fin', header: 'Fin de période', cell: (a) => formatDate(a.currentPeriodEnd) },
    {
      id: 'resiliation',
      header: 'Résiliation',
      cell: (a) =>
        a.cancelAtPeriodEnd ? <Badge tone="terracotta">Programmée au {formatDate(a.currentPeriodEnd)}</Badge> : <span className="muted">—</span>,
    },
    { id: 'debut', header: 'Depuis le', secondaire: true, cell: (a) => formatDate(a.createdAt) },
    {
      id: 'stripe',
      header: 'Stripe',
      cell: (a) => (a.stripeSubscriptionId ? <LienExterne href={lienStripe(mode, `subscriptions/${a.stripeSubscriptionId}`)}>Ouvrir</LienExterne> : '—'),
    },
  ];

  return (
    <>
      <VentesEntete lead="Abonnements en cours, en retard de paiement ou résiliés." />
      <div className="toolbar" role="search">
        <Recherche id="abonnees-eleve" label="E-mail de l’élève" valeur={f.eleve} onChange={(eleve) => modifier({ eleve })} placeholder="E-mail de l’élève" />
        <Filtre id="ab-statut" label="Statut">
          <Select id="ab-statut" value={f.statut} onChange={(e) => modifier({ statut: e.target.value })}>
            <option value="">Tous</option>
            {Object.entries(STATUT_ABONNEMENT).map(([v, s]) => (
              <option key={v} value={v}>
                {s.libelle}
              </option>
            ))}
          </Select>
        </Filtre>
        <Filtre id="ab-formule" label="Formule">
          <Select id="ab-formule" value={f.formule} onChange={(e) => modifier({ formule: e.target.value })}>
            <option value="">Toutes</option>
            {(formules.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Filtre>
        {actifs > 0 && (
          <Button variant="ghost" size="sm" icon={<Filter size={14} aria-hidden="true" />} onClick={reinitialiser}>
            Effacer ({actifs})
          </Button>
        )}
      </div>
      {liste.isError ? (
        <ErreurChargement erreur={liste.error} onReessayer={() => void liste.refetch()} />
      ) : (
        <DataTable
          libelle="Abonnées"
          colonnes={colonnes}
          lignes={liste.data?.subscriptions}
          cleLigne={(a) => a.id}
          chargement={liste.isFetching && !liste.data}
          pagination={liste.data ? { page: liste.data.pagination.page, pages: liste.data.pagination.pages, total: liste.data.pagination.total } : undefined}
          onPage={(page) => modifier({ page: String(page) })}
          vide={
            <EmptyState
              icon={<UserRound size={26} aria-hidden="true" />}
              title={actifs ? 'Aucune abonnée ne correspond' : 'Aucune abonnée'}
              text={actifs ? undefined : 'Les abonnements souscrits depuis l’application apparaîtront ici.'}
            />
          }
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Paiements
// ---------------------------------------------------------------------------

/** Paiements (cahier des charges §7.8) : la liste, et la santé des paiements (webhooks Stripe). */
export function PaymentsLayout() {
  const vue = useVentes();
  const echecs = vue.data?.webhooksEnEchec7j ?? 0;
  return (
    <>
      <VentesEntete lead="Paiements reçus, remboursements et santé des paiements Stripe." />
      <nav className="segmented segmented--liens" aria-label="Vues des paiements" style={{ marginBottom: 14 }}>
        <NavLink to="/ventes/paiements" end>
          Paiements
        </NavLink>
        <NavLink to="/ventes/paiements/webhooks">
          Santé des paiements
          {echecs > 0 && <span className="tabs__count tabs__count--danger">{echecs}</span>}
        </NavLink>
      </nav>
      <Outlet />
    </>
  );
}

const FILTRES_PAIEMENTS = { statut: '', type: '', pack: '', du: '', au: '', eleve: '', rembourses: '', page: '1' };

export function requetePaiements(f: typeof FILTRES_PAIEMENTS): RequetePaiements {
  return {
    page: Number(f.page) || 1,
    limit: 50,
    ...(f.statut ? { status: f.statut as RequetePaiements['status'] } : {}),
    ...(f.type ? { kind: f.type as 'PACK' | 'SUBSCRIPTION' } : {}),
    ...(f.pack ? { packId: f.pack } : {}),
    ...(f.du ? { from: bornesJourParis(f.du, 'debut') } : {}),
    ...(f.au ? { to: bornesJourParis(f.au, 'fin') } : {}),
    ...(f.eleve ? { q: f.eleve } : {}),
    ...(f.rembourses === 'oui' ? { refunded: 'true' as const } : {}),
  };
}

export function PaymentsView() {
  const { valeurs: f, modifier, reinitialiser, actifs } = useFiltresUrl(FILTRES_PAIEMENTS);
  const toast = useToast();
  const vue = useVentes();
  const [export_, setExport] = useState(false);
  const packs = useQuery({ queryKey: ['ventes', 'packs'], queryFn: async () => (await lire(api.GET('/api/admin/billing/packs'))).packs });
  const liste = useQuery({
    queryKey: ['ventes', 'paiements', f],
    queryFn: () => lire(api.GET('/api/admin/billing/transactions', { params: { query: requetePaiements(f) } })),
    placeholderData: keepPreviousData,
  });
  const mode = vue.data?.stripeMode;

  const exporter = async () => {
    setExport(true);
    try {
      const { page: _p, limit: _l, ...query } = requetePaiements(f);
      enregistrer(await lire(api.GET('/api/admin/billing/transactions/export', { params: { query }, parseAs: 'blob' })), nomDate('paiements'));
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setExport(false);
    }
  };

  const colonnes: Colonne<Paiement>[] = [
    { id: 'date', header: 'Date', cell: (p) => <span className="nowrap">{formatDateTime(p.createdAt)}</span> },
    { id: 'eleve', header: 'Élève', cell: (p) => <Eleve user={p.user} /> },
    {
      id: 'offre',
      header: 'Offre',
      cell: (p) => (
        <div>
          <div>{p.pack?.name ?? p.plan?.name ?? '—'}</div>
          <div className="cell-sub">{p.kind === 'PACK' ? 'Pack' : 'Abonnement'}</div>
        </div>
      ),
    },
    {
      id: 'montant',
      header: 'Montant',
      align: 'right',
      cell: (p) => (
        <div>
          <span className="num">{formatEuros(p.amountCents)}</span>
          {p.refundedCents > 0 && <div className="cell-sub neg">−{formatEuros(p.refundedCents)} remboursé</div>}
        </div>
      ),
    },
    {
      id: 'credits',
      header: 'Crédits',
      align: 'right',
      cell: (p) => (
        <div>
          <span className="num">{formatNombre(p.creditsGranted)}</span>
          {p.creditsToRevoke > 0 && <div className="cell-sub">−{formatNombre(p.creditsRevoked)} retirés</div>}
          {p.creditsToRevoke > p.creditsRevoked && (
            <div className="cell-sub">
              <Link className="link" to={`/eleves/${p.user.id}?onglet=credits`} title="Crédits déjà consommés au remboursement : à régulariser à la main si besoin.">
                {formatNombre(p.creditsToRevoke - p.creditsRevoked)} déjà consommés
              </Link>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'statut',
      header: 'Statut',
      cell: (p) => {
        const s = STATUT_PAIEMENT[p.status] ?? { libelle: p.status, ton: 'neutral' as const };
        return (
          <div>
            <Badge tone={s.ton}>{p.status === 'SUCCEEDED' && p.refundedCents > 0 ? 'Remboursé en partie' : s.libelle}</Badge>
            {p.refundedAt && <div className="cell-sub">le {formatDate(p.refundedAt)}</div>}
          </div>
        );
      },
    },
    {
      id: 'stripe',
      header: 'Stripe',
      cell: (p) =>
        p.stripePaymentIntentId ? (
          <LienExterne href={lienStripe(mode, `payments/${p.stripePaymentIntentId}`)}>Paiement</LienExterne>
        ) : p.stripeInvoiceId ? (
          <LienExterne href={lienStripe(mode, `invoices/${p.stripeInvoiceId}`)}>Facture</LienExterne>
        ) : (
          '—'
        ),
    },
  ];

  const t = liste.data?.totals;

  return (
    <>
      <div className="toolbar" role="search">
        <Recherche id="paiements-eleve" label="E-mail de l’élève" valeur={f.eleve} onChange={(eleve) => modifier({ eleve })} placeholder="E-mail de l’élève" />
        <Filtre id="p-statut" label="Statut">
          <Select id="p-statut" value={f.statut} onChange={(e) => modifier({ statut: e.target.value })}>
            <option value="">Tous</option>
            {Object.entries(STATUT_PAIEMENT).map(([v, s]) => (
              <option key={v} value={v}>
                {s.libelle}
              </option>
            ))}
          </Select>
        </Filtre>
        <Filtre id="p-type" label="Type">
          <Select id="p-type" value={f.type} onChange={(e) => modifier({ type: e.target.value, ...(e.target.value === 'SUBSCRIPTION' ? { pack: '' } : {}) })}>
            <option value="">Tous</option>
            <option value="PACK">Packs</option>
            <option value="SUBSCRIPTION">Abonnements</option>
          </Select>
        </Filtre>
        {f.type !== 'SUBSCRIPTION' && (
          <Filtre id="p-pack" label="Pack">
            <Select id="p-pack" value={f.pack} onChange={(e) => modifier({ pack: e.target.value })}>
              <option value="">Tous</option>
              {(packs.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Filtre>
        )}
        <Filtre id="p-du" label="Du">
          <Input id="p-du" type="date" value={f.du} max={f.au || jourParis()} onChange={(e) => modifier({ du: e.target.value })} />
        </Filtre>
        <Filtre id="p-au" label="Au">
          <Input id="p-au" type="date" value={f.au} min={f.du || undefined} max={jourParis()} onChange={(e) => modifier({ au: e.target.value })} />
        </Filtre>
        <Filtre id="p-remb" label="Remboursements">
          <Select id="p-remb" value={f.rembourses} onChange={(e) => modifier({ rembourses: e.target.value })}>
            <option value="">Tous</option>
            <option value="oui">Remboursés (même en partie)</option>
          </Select>
        </Filtre>
        {actifs > 0 && (
          <Button variant="ghost" size="sm" icon={<Filter size={14} aria-hidden="true" />} onClick={reinitialiser}>
            Effacer ({actifs})
          </Button>
        )}
        <span className="toolbar__spacer" />
        <Button icon={<Download size={15} aria-hidden="true" />} onClick={() => void exporter()} loading={export_}>
          Exporter pour la comptabilité
        </Button>
      </div>

      {t && liste.data && (
        <p className="totaux" aria-live="polite">
          <strong>{formatNombre(liste.data.pagination.total)}</strong> paiement{liste.data.pagination.total > 1 ? 's' : ''} · montant{' '}
          <strong>{formatEuros(t.amountCents)}</strong>
          {t.refundedCents > 0 && (
            <>
              {' '}
              · remboursé <strong>{formatEuros(t.refundedCents)}</strong> · net <strong>{formatEuros(t.amountCents - t.refundedCents)}</strong>
            </>
          )}{' '}
          · {formatCredits(t.creditsGranted)} accordés
        </p>
      )}

      {liste.isError ? (
        <ErreurChargement erreur={liste.error} onReessayer={() => void liste.refetch()} />
      ) : (
        <DataTable
          libelle="Paiements"
          colonnes={colonnes}
          lignes={liste.data?.transactions}
          cleLigne={(p) => p.id}
          chargement={liste.isFetching && !liste.data}
          pagination={liste.data ? { page: liste.data.pagination.page, pages: liste.data.pagination.pages, total: liste.data.pagination.total } : undefined}
          onPage={(page) => modifier({ page: String(page) })}
          vide={<EmptyState icon={<CreditCard size={26} aria-hidden="true" />} title={actifs ? 'Aucun paiement ne correspond' : 'Aucun paiement'} />}
        />
      )}
      <p className="note">
        Un remboursement se fait dans Stripe : les crédits du paiement sont alors retirés automatiquement, au prorata du montant remboursé et sans
        descendre sous zéro. Les crédits déjà consommés sont signalés dans « À traiter ».
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Santé des paiements
// ---------------------------------------------------------------------------

const FILTRES_WEBHOOKS = { statut: '', type: '', page: '1' };

export function WebhooksView() {
  const { valeurs: f, modifier, reinitialiser, actifs } = useFiltresUrl(FILTRES_WEBHOOKS);
  const vue = useVentes();
  const liste = useQuery({
    queryKey: ['ventes', 'webhooks', f],
    queryFn: () =>
      lire(
        api.GET('/api/admin/billing/webhooks', {
          params: {
            query: {
              page: Number(f.page) || 1,
              limit: 50,
              ...(f.statut ? { status: f.statut as RequeteWebhooks['status'] } : {}),
              ...(f.type ? { type: f.type } : {}),
            },
          },
        })
      ),
    placeholderData: keepPreviousData,
  });
  const d = liste.data;
  const mode = d?.stripeMode ?? vue.data?.stripeMode;
  const dernier = vue.data?.dernierWebhook;

  const colonnes: Colonne<Webhook>[] = [
    { id: 'date', header: 'Reçu le', cell: (w) => <span className="nowrap">{formatDateTime(w.createdAt)}</span> },
    { id: 'type', header: 'Événement', cell: (w) => <code className="mono-sm">{w.type}</code> },
    {
      id: 'statut',
      header: 'Statut',
      cell: (w) => {
        const s = STATUT_WEBHOOK[w.status] ?? { libelle: w.status, ton: 'neutral' as const };
        return <Badge tone={s.ton}>{s.libelle}</Badge>;
      },
    },
    { id: 'message', header: 'Message', cell: (w) => <span className="cell-sub">{w.message ?? '—'}</span> },
    { id: 'duree', header: 'Durée', align: 'right', secondaire: true, cell: (w) => (w.durationMs !== null ? `${formatNombre(w.durationMs)} ms` : '—') },
    {
      id: 'stripe',
      header: 'Stripe',
      cell: (w) => (w.eventId ? <LienExterne href={lienStripe(mode, `events/${w.eventId}`)}>Ouvrir</LienExterne> : '—'),
    },
  ];

  return (
    <>
      <div className="kpis kpis--compact" style={{ marginBottom: 14 }}>
        <Card className="kpi">
          <div className="kpi__label">Échecs à traiter (7 jours)</div>
          <div className={`kpi__value ${d && d.unresolvedFailures7d > 0 ? 'neg' : ''}`}>
            {d ? formatNombre(d.unresolvedFailures7d) : '—'}
          </div>
          <div className="kpi__hint">
            {d && d.unresolvedFailures7d > 0 ? (
              <>
                <AlertTriangle size={12} aria-hidden="true" /> Un paiement a pu ne pas créditer l’élève.
              </>
            ) : (
              <>
                <CheckCircle2 size={12} aria-hidden="true" /> Aucun échec non rattrapé.
              </>
            )}
          </div>
        </Card>
        <Card className="kpi">
          <div className="kpi__label">Dernier webhook reçu</div>
          <div className="kpi__value kpi__value--texte">{dernier ? formatDateTime(dernier.createdAt) : 'Aucun'}</div>
          <div className="kpi__hint">{dernier ? `${dernier.type} · ${STATUT_WEBHOOK[dernier.status]?.libelle ?? dernier.status}` : 'Rien n’a encore été reçu de Stripe.'}</div>
        </Card>
        <Card className="kpi">
          <div className="kpi__label">Compte Stripe</div>
          <div className="kpi__value kpi__value--texte">{mode === 'live' ? 'Paiements réels' : mode === 'test' ? 'Mode test' : 'Non configuré'}</div>
          <div className="kpi__hint">Liens « Ouvrir » vers le tableau de bord de ce mode.</div>
        </Card>
      </div>

      <div className="toolbar" role="search">
        <Filtre id="w-statut" label="Statut">
          <Select id="w-statut" value={f.statut} onChange={(e) => modifier({ statut: e.target.value })}>
            <option value="">Tous</option>
            <option value="problems">À traiter (échecs, signatures invalides)</option>
            {Object.entries(STATUT_WEBHOOK).map(([v, s]) => (
              <option key={v} value={v}>
                {s.libelle}
              </option>
            ))}
          </Select>
        </Filtre>
        <Recherche id="w-type" label="Type d’événement Stripe" valeur={f.type} onChange={(type) => modifier({ type })} placeholder="Type (ex. : invoice.paid)" />
        {actifs > 0 && (
          <Button variant="ghost" size="sm" icon={<Filter size={14} aria-hidden="true" />} onClick={reinitialiser}>
            Effacer ({actifs})
          </Button>
        )}
      </div>

      {liste.isError ? (
        <ErreurChargement erreur={liste.error} onReessayer={() => void liste.refetch()} />
      ) : (
        <DataTable
          libelle="Webhooks Stripe reçus"
          colonnes={colonnes}
          lignes={d?.entries}
          cleLigne={(w) => w.id}
          chargement={liste.isFetching && !d}
          pagination={d ? { page: d.pagination.page, pages: d.pagination.pages, total: d.pagination.total } : undefined}
          onPage={(page) => modifier({ page: String(page) })}
          vide={
            <EmptyState
              icon={<Webhook size={26} aria-hidden="true" />}
              title={actifs ? 'Aucun webhook ne correspond' : 'Aucun webhook reçu'}
              text={actifs ? undefined : 'Le journal commence avec cette version : les webhooks reçus à partir de maintenant apparaîtront ici.'}
            />
          }
        />
      )}
      <Section title="En cas d’échec">
        <ul className="note liste-aide">
          <li>Stripe relivre un webhook en échec pendant plusieurs jours : un échec suivi d’un succès du même événement n’est plus compté.</li>
          <li>« Signature invalide » : le secret du webhook configuré sur le serveur ne correspond pas à l’endpoint Stripe du bon mode.</li>
          <li>Une élève a payé sans recevoir ses crédits : ajoutez-les depuis sa fiche (onglet Crédits), en citant le paiement dans le motif.</li>
        </ul>
      </Section>
    </>
  );
}
